import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PLANS, getPlanPrice, type PaidTier, type DurationMonths } from '../../config/plans';
import { PlanCard } from '../plans/PlanCard';
import { Button, SegmentedControl, buttonClasses } from '../ui';

export interface PricingProps {
  /** Scrolls to and focuses the hero PLZ input — the Free plan needs no separate signup. */
  onFreeSelect: () => void;
}

type DurationOption = '1' | '12';

/**
 * Preise: `PlanCard`s from the shared `PLANS` config (never hard-coded prices), a
 * Laufzeit toggle, and CTAs that navigate (register/upgrade) rather than pay directly.
 */
export const Pricing: React.FC<PricingProps> = ({ onFreeSelect }) => {
  const { isAuthenticated } = useAuth();
  const [duration, setDuration] = useState<DurationOption>('12');
  const months = Number(duration) as DurationMonths;

  const paidHref = (tier: PaidTier) => {
    const price = getPlanPrice(tier, months);
    const upgradePath = `/dashboard/upgrade?plan=${price.planId}`;
    return isAuthenticated ? upgradePath : `/register?redirect=${encodeURIComponent(upgradePath)}`;
  };

  return (
    <section id="pricing" className="scroll-mt-20 border-b border-line bg-card py-16 md:py-24">
      <div className="mx-auto w-full max-w-wide px-4 md:px-6">
        <h2 className="font-display text-2xl text-ink md:text-3xl">Einfache, faire Preise</h2>
        <p className="mt-2 max-w-narrow text-muted">Kostenlos starten, jederzeit upgraden.</p>

        <div className="mt-6">
          <SegmentedControl
            label="Laufzeit"
            showLabel
            value={duration}
            onChange={setDuration}
            options={[
              { value: '1', label: '1 Monat' },
              { value: '12', label: '12 Monate' },
            ]}
          />
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {/* Highlighted (Pro) gets the primary CTA; Free and Premium stay secondary (V2). */}
          <PlanCard
            plan={PLANS.free}
            cta={
              <Button onClick={onFreeSelect} variant="secondary" fullWidth>
                Kostenlos starten
              </Button>
            }
          />
          <PlanCard
            plan={PLANS.pro}
            months={months}
            highlighted
            cta={
              <div>
                <Link to={paidHref('pro')} className={buttonClasses('primary', 'md', 'w-full')}>
                  Pro wählen
                </Link>
                <p className="mt-2 text-sm text-muted">Erst Konto anlegen, dann Plan aktivieren.</p>
              </div>
            }
          />
          <PlanCard
            plan={PLANS.premium}
            months={months}
            cta={
              <div>
                <Link to={paidHref('premium')} className={buttonClasses('secondary', 'md', 'w-full')}>
                  Premium wählen
                </Link>
                <p className="mt-2 text-sm text-muted">Erst Konto anlegen, dann Plan aktivieren.</p>
              </div>
            }
          />
        </div>
      </div>
    </section>
  );
};

export default Pricing;
